import { observable, computed, action, runInAction } from 'mobx';
import auth0 from 'auth0-js';
import axios from 'axios';

import storageService from '../services/storage.service';

const AUTH_CONFIG = {
  domain: process.env.REACT_APP_AUTH_DOMAIN,
  clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
  callbackUrl: `${process.env.REACT_APP_DOMAIN}/callback`,
};

class AuthStore {
  @observable accessToken;
  @observable auth0;
  @observable idToken;
  @observable email;
  @observable name;
  @observable expiresAt;
  @observable authenticating = true;
  @observable reauthenticating = true;
  @observable notifications = [];
  @observable user = { sub: '', email: '', email_verified: false };
  @observable accountInfo = {
    Email: '',
    Name: '',
    NotificationPref: [
      {
        Category: 'Organization Setup',
        Push: true,
        Email: true,
      },
      {
        Category: 'Invitations',
        Push: true,
        Email: true,
      },
      {
        Category: 'Deal Status',
        Push: true,
        Email: false,
      },
      {
        Category: 'Transaction Status',
        Push: true,
        Email: false,
      },
    ],
  };

  constructor() {
    this.auth0 = new auth0.WebAuth({
      domain: AUTH_CONFIG.domain,
      clientID: AUTH_CONFIG.clientId,
      redirectUri: AUTH_CONFIG.callbackUrl,
      responseType: 'token id_token',
      scope: 'openid email profile',
      audience: process.env.REACT_APP_API,
    });
  }

  startAutoRenewSession = () => {
    setInterval(() => {
      this.renewSession();
    }, 1000 * 60 * 31);
  };

  login = () => {
    this.auth0.authorize({
      prompt: 'login',
      mode: 'login',
    });
  };

  signup = () => {
    this.auth0.authorize({
      prompt: 'login',
      mode: 'signUp',
    });
  };

  xeroAuth = () => {
    this.auth0.authorize({
      connection: 'Xero',
    });
  };

  sendRefCode = async () => {
    if (storageService.getItem('ref_code')) {
      await this.request.post(
        `${process.env.REACT_APP_API}/api/send-referral-code`,
        {
          refCode: storageService.getItem('ref_code'),
        },
      );
      storageService.removeItem('ref_code');
    }
  };

  handleAuthentication = history => {
    this.auth0.parseHash(async (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
        this.sendRefCode();
        let pushLocation = '/select-organization';
        try {
          const { data = {} } = await this.request.get(
            `${process.env.REACT_APP_API}/api/organization`,
          );
          pushLocation = data.OrgId ? '/dashboard' : '/select-organization';
          data.OrgId && storageService.setItem('activeOrg', data.OrgId);
        } catch (e) {
          console.error(e);
        }
        history.push(pushLocation);
      } else if (err) {
        console.error(err);
        // alert(`Error: ${err.errorDescription}`);
        const errorPayload = err.errorDescription.split(', ');
        history.push('/login', {
          error: errorPayload[0],
          sub: errorPayload[1],
          email: errorPayload[2],
          name: errorPayload[3],
        });
        return false;
      }
    });
  };

  sendServerEmail = async () => {
    await axios.post(
      `${process.env.REACT_APP_API}/api/auth`,
      {
        email: this.email,
        name: this.name,
      },
      {
        headers: {
          Authorization: `bearer ${this.getAccessToken()}`,
        },
      },
    );
  };

  getAccessToken = () => {
    return this.accessToken;
  };

  getIdToken = () => {
    return this.idToken;
  };

  setSession = authResult => {
    this.startAutoRenewSession();
    // Set isLoggedIn flag in localStorage
    storageService.setItem('isLoggedIn', 'true');

    // Set the time that the access token will expire at
    const expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
    this.accessToken = authResult.accessToken;
    this.idToken = authResult.idToken;
    this.email = authResult.idTokenPayload.email;
    this.name = authResult.idTokenPayload.name;

    this.expiresAt = expiresAt;

    // send Token info to server
    this.auth0.client.userInfo(authResult.accessToken, (err, user) => {
      if (err) {
        console.log(err);
      } else if (user) {
        this.user = user;
      }
    });
    this.sendServerEmail();
  };

  renewSession = () => {
    this.reauthenticating = true;
    this.auth0.checkSession({}, async (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
      } else if (err) {
        this.logout({ inactivity: true });
        console.error(err);
        // alert(
        //   `Could not get a new token (${err.error}: ${err.error_description}).`,
        // );
      }
      this.reauthenticating = false;
      this.authenticating = false;
    });
  };

  logout = (type = {}) => {
    const { inactivity } = type;

    // Remove tokens and expiry time
    this.accessToken = null;
    this.idToken = null;
    this.email = null;
    this.name = null;
    this.expiresAt = 0;
    this.user = null;
    this.accountInfo = null;

    // Remove isLoggedIn flag from localStorage
    storageService.removeItem('isLoggedIn');
    storageService.removeItem('activeOrg');
    const loginString = `${window.location.origin}/login${
      inactivity ? `?inactivity` : ''
    }`;

    this.auth0.logout({
      returnTo: loginString,
    });
  };

  @computed
  get request() {
    const token = this.getAccessToken();
    return {
      get: (url, options = {}) =>
        axios.get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          ...options,
        }),
      post: (url, data, options = {}) =>
        axios.post(url, data, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          ...options,
        }),
      put: (url, data, options = {}) =>
        axios.put(url, data, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          ...options,
        }),
      delete: (url, options = {}) =>
        axios.delete(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          ...options,
        }),
    };
  }

  @computed get isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    const { expiresAt } = this;
    return new Date().getTime() < expiresAt;
  }

  @action
  stopAuthenticating = () => {
    this.authenticating = false;
    this.reauthenticating = false;
  };

  @action
  setNotifications = n => {
    this.notifications = n;
  };

  @action
  addNotification = n => {
    this.notifications = this.notifications.concat(n);
  };

  @action
  async retrieveUser() {
    try {
      const data = await this.fetchUser();
      const { UserId, Name, Email, NotificationPref } = data;
      runInAction(() => {
        this.accountInfo = {
          UserId,
          Name,
          Email,
          NotificationPref: NotificationPref || [
            {
              Category: 'Organization Setup',
              Push: true,
              Email: true,
            },
            {
              Category: 'Invitations',
              Push: true,
              Email: true,
            },
            {
              Category: 'Deal Status',
              Push: true,
              Email: false,
            },
            {
              Category: 'Transaction Status',
              Push: true,
              Email: false,
            },
          ],
        };
      });
    } catch (error) {
      console.error(error);
    }
  }

  fetchUser = async () => {
    try {
      const { data } = await this.request.get(
        `${process.env.REACT_APP_API}/api/user`,
      );
      return data;
    } catch (err) {
      console.log('something went wrong:', err);
    }
  };
}

const auth = new AuthStore();

export default auth;
